import React, 
       { useState, 
         useRef 
       }                              from 'react';
import { Box, 
         TextField, 
         Typography, 
         Button, 
         Alert, 
         IconButton, 
         Tooltip, 
         Theme 
       }                              from '@mui/material';
import { ApiErrorInfo }               from 'types';
import { darken }                     from '@mui/material/styles';
import EditIcon                       from '@mui/icons-material/Edit';
import RestoreIcon                    from '@mui/icons-material/Restore';
import { IIMAGE_SPEC, 
         IMAGE_SPEC 
       }                              from 'features/imageCrop/ImageSpec';
import ImageCropDialog                from 'features/imageCrop/ImageCropComponent';
import { BglPrimaryOutlinedButton }   from 'components/button/BglButtons';
import CircularProgress               from '@mui/material/CircularProgress';
import { useGlobalConfig } from 'context/GlobalContext';

interface ImageUploadSectionProps {
  isDisabled: boolean;
  imageType: IIMAGE_SPEC;
  currentImageUrl?: string;
  onImageUpdate: (blob: Blob) => Promise<ApiErrorInfo | undefined>;
  isModified?: boolean;
}

export const ImageUploadSection: React.FC<ImageUploadSectionProps> = ({
  isDisabled,
  imageType,
  currentImageUrl,
  onImageUpdate,
  isModified = false,
}) => {

  const { rc } = useGlobalConfig()
  const [imageUrl, setImageUrl] = useState('');
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditingCurrent, setIsEditingCurrent] = useState(false);
  const originalImageUrl = useRef<string | undefined>(currentImageUrl);
  const [hasBeenModified, setHasBeenModified] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update originalImageUrl ref when currentImageUrl changes
  React.useEffect(() => {
    if (currentImageUrl && !hasBeenModified) {
      originalImageUrl.current = currentImageUrl;
    }
  }, [currentImageUrl, hasBeenModified]);

  const spec = IMAGE_SPEC[imageType];
  const aspectRatio = spec.minWidth / spec.minHeight;

  // Style for modified label
  const getModifiedLabelStyle = () => ({
    position: 'relative',
    '&::after': isModified ? {
      content: '""',
      position: 'absolute',
      right: '-16px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: (theme: Theme) => {
        if (theme.palette.mode === 'light') {
          return 'rgba(196, 255, 176, 1)';
        }
        return darken('rgba(196, 255, 176, 1)', 0.3);
      },
    } : {},
  });

  const validateImageSize = (width: number, height: number): boolean => {
    return width >= spec.minWidth && height >= spec.minHeight;
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlSubmit = () => {
    setError(null);
    if (!imageUrl) return;

    if (!validateUrl(imageUrl)) {
      setError('Please enter a valid URL');
      return;
    }

    // Create a temporary image to check dimensions
    const img = new Image();
    img.onload = () => {
      if (!validateImageSize(img.naturalWidth, img.naturalHeight)) {
        setError(`Image must be at least ${spec.minWidth}x${spec.minHeight} pixels`);
        return;
      }
      setIsEditingCurrent(false);
      setIsCropDialogOpen(true);
    };
    img.onerror = () => {
      setError('Failed to load image from URL');
    };
    img.src = imageUrl;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      // Reset input value so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      // Reset input value so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check image dimensions
    const img = new Image();
    img.onload = () => {
      if (!validateImageSize(img.naturalWidth, img.naturalHeight)) {
        setError(`Image must be at least ${spec.minWidth}x${spec.minHeight} pixels`);
        // Reset input value even on error so same file can be selected again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setSelectedFile(file);
      setIsEditingCurrent(false);
      setIsCropDialogOpen(true);
      // Reset input value so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    img.onerror = () => {
      setError('Failed to load image file');
      // Reset input value even on error so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleEditCurrent = () => {
    setIsEditingCurrent(true);
    setIsCropDialogOpen(true);
  };

  const handleReset = async () => {
    if (!originalImageUrl.current) return;

    try {
      setIsUpdating(true);
      
      const proxyUrl = `/global/proxy-image?url=${encodeURIComponent(originalImageUrl.current)}`;
      const blob = await rc.cmsApiClient.getBlob(proxyUrl);
      
      const error = await onImageUpdate(blob);
      if (error) {
        setError(error.message);
      }  
      setHasBeenModified(false);
  
    } catch (err) {
      setError('Failed to restore original image');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCropComplete = async (blob?: Blob) => {

    setIsCropDialogOpen(false);

    if (blob) {
      setIsUpdating(true);
      const error = await onImageUpdate(blob);
      if (error) {
        setError(error.message);
      }  
      setIsUpdating(false);
      setHasBeenModified(true);

    } else {
      setError(null);
    }

    setSelectedFile(null);
    setImageUrl('');
    setIsEditingCurrent(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {isUpdating && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          bgcolor: 'rgba(255,255,255,0.7)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress />
        </Box>
      )}
      <Typography 
        variant="bglBody2" 
        sx={{ 
          width: 'fit-content',
          textAlign: 'left', 
          fontWeight: 'bold',
          ...getModifiedLabelStyle()
        }}
      >
        {IMAGE_SPEC[imageType].name} ({spec.minWidth} x {spec.minHeight})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {currentImageUrl && (
        <Box sx={{ 
          width: '100%', 
          maxWidth: spec.minWidth,
          position: 'relative',
          '&:hover .image-actions': {
            opacity: 1,
          }
        }}>
          <img
            src={currentImageUrl}
            alt={imageType}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              backgroundColor: 'grey',
            }}
          />
          <Box 
            className="image-actions"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 1,
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
          >
            <Tooltip title="Edit Image">
              <IconButton
                disabled={isDisabled}
                onClick={handleEditCurrent}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {/* {hasBeenModified && originalImageUrl.current && (
              <Tooltip title="Restore Original">
                <IconButton
                  onClick={handleReset}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }
                  }}
                >
                  <RestoreIcon />
                </IconButton>
              </Tooltip>
            )} */}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Image URL"
          value={imageUrl}
          disabled={isDisabled}
          onChange={(e) => {
            setImageUrl(e.target.value);
            setError(null);
          }}
          sx={{ flex: 1 }}
          error={!!error}
        />
        <Button
          variant="contained"
          onClick={handleUrlSubmit}
          disabled={!imageUrl || isDisabled}
        >
          Load URL
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="bglBody2">OR</Typography>
        <BglPrimaryOutlinedButton
          component="label"
          size="small"
          disabled={isDisabled}
        >
          Upload File
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileSelect}
          />
        </BglPrimaryOutlinedButton>
      </Box>

      <ImageCropDialog
        file={selectedFile ?? undefined}
        open={isCropDialogOpen}
        srcUrl={isEditingCurrent ? currentImageUrl! : (selectedFile ? URL.createObjectURL(selectedFile) : imageUrl)}
        minWidth={spec.minWidth}
        minHeight={spec.minHeight}
        aspectRatio={aspectRatio}
        imgMaxSize={5 * 1024 * 1024} // 5MB
        onClose={handleCropComplete}
      />
    </Box>
  );
}; 